import path from "node:path";
import type { Response } from "@iflyrpa/share";
import { downloadImage, getFilenameFromUrl, success } from "@iflyrpa/share";

import { copyHtmlToClipboard, getRandomInRange, PopupListener, TimeFormatter } from "../../utils";
import { COVER_TYPE, getAddTypeValue, type PublishAction } from "./index";

const GenAB = require("../../utils/ttABEncrypt") as (headers: string, data: string) => string;

export const rpaAction: PublishAction = async (task, params) => {
    const tmpCachePath = task.getTmpPath();

    const commonCookies = {
        path: "/",
        secure: true,
        domain: "toutiao.com",
        url: "https://mp.toutiao.com",
        httpOnly: true,
    };

    const page = await task.createPage({
        show: task.debug,
        url: params.url || "https://mp.toutiao.com/profile_v4/graphic/publish",
        cookies: params.cookies?.map((it) => ({ ...it, ...commonCookies })) || [],
    });

    // 0. 校验登录
    try {
        await page.waitForSelector(".publish-editor", { state: "visible" });
    } catch (_error) {
        return {
            code: 414,
            message: `头条号登录状态失效，请重新绑定账号后${params.saveType === "draft" ? "同步" : "发布"}。`,
            data: page.url(),
        };
    }

    const interceptUrls = ["/mp/agw/article/publish"];

    await page.route("**", async (route, request) => {
        const url = new URL(request.url());
        if (interceptUrls.some((pattern) => url.pathname.includes(pattern))) {
            const headers = {
                ...request.headers(),
                "user-agent":
                    "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.36", //ua参数固定，不可修改
            };

            const generatorFormData = (data: Record<string, unknown>) => {
                return Object.entries(data)
                    .map(([key, value]) => {
                        const encodedKey = encodeURIComponent(key);
                        const encodedValue = encodeURIComponent(String(value));
                        return `${encodedKey}=${encodedValue}`;
                    })
                    .join("&");
            };

            const postHeaders = generatorFormData({
                source: "mp",
                type: "article",
                aid: 1231,
                mp_publish_ab_val: 0,
                msToken: url.searchParams.get("msToken"),
            });
            const postData = request.postData() || "";
            const a_bogus = GenAB(postHeaders, postData);

            url.searchParams.set("aid", "1231");
            url.searchParams.set("mp_publish_ab_val", "0");
            url.searchParams.set("a_bogus", a_bogus);

            await route.continue({ url: url.toString(), headers: headers });
        } else {
            await route.continue();
        }
    });

    //弹窗监听检测
    PopupListener(page, ".byte-modal.publish-exclusive-modal .title", "激励", ".byte-modal-close-icon", {
        checkInterval: 1000,
        stopAfterClick: false,
    });

    // 1. 填写标题
    const titleInstance = page.locator(".publish-editor-title-wrapper .publish-editor-title textarea");
    await titleInstance.click();
    await titleInstance.fill(params.title);

    // 2. 填写正文
    await copyHtmlToClipboard(page, params.content);
    // 定位目标元素并聚焦
    await page.click(".publish-editor .syl-editor-wrap .ProseMirror");
    // 模拟Ctrl+V粘贴
    await page.keyboard.press("Control+V");

    // 3. 封面
    const coverTypeValue = COVER_TYPE[params.settingInfo.toutiaoCoverType];
    const coverContainer = page.locator(".form-container .edit-cell").filter({ hasText: "展示封面" });
    // 滚动到可视区域
    await coverContainer.evaluate((node) => node.scrollIntoView());
    await coverContainer.locator(`.article-cover label.byte-radio:has(input[value="${coverTypeValue}"])`).click();

    let coverUrls: string[] = [];
    if (params.settingInfo.toutiaoCoverType === "single") {
        coverUrls = [params.settingInfo.toutiaoSingleCover];
    } else if (params.settingInfo.toutiaoCoverType === "multiple") {
        coverUrls = params.settingInfo.toutiaoMultCover;
    }
    // 获取待上传图片的本地路径
    const images = await Promise.all(
        coverUrls.map((url) => {
            const fileName = getFilenameFromUrl(url);
            return downloadImage(url, path.join(tmpCachePath, fileName));
        }),
    );

    const coverUploadEle = coverContainer
        .locator(".article-cover .article-cover-images")
        .locator(".article-cover-img-wrap, .article-cover-add");
    const coverUploadCount = await coverUploadEle.count();

    for (let i = 0; i < coverUploadCount; i++) {
        if (!images[i]) {
            continue;
        }

        const ele = coverUploadEle.nth(i);
        // 如果存在图片则替换
        if ((await ele.locator("img").count()) > 0) {
            await ele.locator(".article-cover-img-menu a").filter({ hasText: "替换" }).click();
        } else {
            // 如果不存在图片则点击上传
            await ele.click();
        }

        // 上传图片
        const drawerContainer = page.locator(".byte-drawer-wrapper:not(.byte-drawer-wrapper-hide)");
        await drawerContainer
            .locator(".byte-tabs-header .byte-tabs-header-title")
            .filter({ hasText: "上传图片" })
            .click();
        const fileChooserPromise = page.waitForEvent("filechooser");
        await page.waitForTimeout(200);
        await drawerContainer.locator(".upload-image-panel button.byte-btn").filter({ hasText: "本地上传" }).click();
        const fileChooser = await fileChooserPromise;
        await fileChooser.setFiles([images[i]]);

        // 等待上传完成（图片上传到服务端需要时间，disabled 状态解除后才可点击）
        const confirmBtn = drawerContainer
            .locator(".footer .confirm-btns button:not(.byte-btn-disabled)")
            .filter({ hasText: "确定" });
        await confirmBtn.waitFor({ state: "visible", timeout: 90000 });
        await confirmBtn.click();

        await page.waitForTimeout(getRandomInRange(500, 1000));
    }

    // 4. 添加位置
    if (params.settingInfo.toutiaoLocation) {
        const locationContainer = page.locator(".form-container .edit-cell").filter({ hasText: "添加位置" });
        await locationContainer.locator(".position-select").click();
        await locationContainer
            .locator(".position-select .byte-select-view-search input")
            .fill(params.settingInfo.toutiaoLocation.label);
        const poperInstance = page.locator(".byte-trigger .byte-select-popup .byte-select-option");
        await poperInstance.first().waitFor();
        await poperInstance.first().click();
    }

    // 5. 投放广告
    const adContainer = page
        .locator(".form-container .edit-cell .edit-label")
        .filter({ hasText: "投放广告" })
        .locator("xpath=..");

    // 判断是否存在投放广告的选项
    const isAdOptionVisible = await adContainer.isVisible();

    if (isAdOptionVisible) {
        const adRadio = adContainer.locator(
            `label.byte-radio:has(input[value="${getAddTypeValue(params.settingInfo.toutiaoAd)}"])`,
        );
        // 滚动到元素位置，避免被底部工具栏遮挡
        await adRadio.scrollIntoViewIfNeeded();
        await page.waitForTimeout(300);
        // 使用 force 点击，避免被遮挡元素拦截
        await adRadio.click({ force: true });
    }

    // 6. 声明首发
    const exclusiveContainer = page
        .locator(".form-container .edit-cell .edit-label")
        .filter({ hasText: "声明首发" })
        .locator("xpath=..");
    if (params.settingInfo.toutiaoOriginal?.includes("exclusive") || params.settingInfo.toutiaoExclusive) {
        exclusiveContainer.locator(".exclusive .exclusive-checkbox-wraper .byte-checkbox").check();
    } else {
        exclusiveContainer.locator(".exclusive .exclusive-checkbox-wraper .byte-checkbox").uncheck();
    }

    // 作品声明
    const claim = params.settingInfo.toutiaoClaim ? params.settingInfo.toutiaoClaim.type : 5;
    switch (claim) {
        // case 1:
        // 	await page.locator(".source-wrap .byte-checkbox",{hasText: "引用站内"}).locator(".byte-checkbox-mask").check();
        // 	if(!params.settingInfo.toutiaoClaim?.source_author_name) return{ code: 500, message: "请填写作者账户名", data: "" };
        // 	await page.locator(".source-form-item",{hasText: "账号"}).locator(".byte-select-view-search input").fill(params.settingInfo.toutiaoClaim.source_author_name)
        // 	break;
        case 3:
            await page
                .locator(".source-wrap .byte-checkbox", { hasText: "引用AI" })
                .locator(".byte-checkbox-mask")
                .check();

            break;
        case 4:
            await page
                .locator(".source-wrap .byte-checkbox", { hasText: "取材网络" })
                .locator(".byte-checkbox-mask")
                .check();

            break;
        case 5:
            await page
                .locator(".source-wrap .byte-checkbox", { hasText: "个人观点，仅供参考" })
                .locator(".byte-checkbox-mask")
                .check();

            break;
        case 6:
            await page
                .locator(".source-wrap .byte-checkbox", { hasText: "虚构演绎，故事经历" })
                .locator(".byte-checkbox-mask")
                .check();

            break;
        default:
            await page
                .locator(".source-wrap .byte-checkbox", { hasText: "个人观点，仅供参考" })
                .locator(".byte-checkbox-mask")
                .check();

            break;
    }

    // 7. 保存草稿 / 发布
    // biome-ignore-start lint/suspicious/noAsyncPromiseExecutor: 临时保留async
    const articleId = await new Promise<string>(async (resolve) => {
        const handleResponse = async (response: Response) => {
            const url = response.url();
            if (url.includes("/mp/agw/article/publish")) {
                const jsonResponse = await response.json();
                page.off("response", handleResponse);
                resolve(jsonResponse.data.pgc_id);
            }
        };

        page.on("response", handleResponse);

        if (params.settingInfo.timer && params.saveType === "publish") {
            // 点击“定时发布”按钮
            await page.locator(".publish-footer button").filter({ hasText: "定时发布" }).click();
            await page.waitForSelector("button.publish-btn:not(.byte-btn-loading) >> nth=1", { state: "attached" });

            // 选择日期
            await page.locator(".byte-modal-content .day-select .byte-select-view-value").click();
            await page
                .locator("li.byte-select-option", {
                    hasText: TimeFormatter.format(params.settingInfo.timer, "MM月dd日"),
                })
                .click();
            await page.waitForTimeout(200);

            // 选择小时
            await page.locator(".byte-modal-content .hour-select .byte-select-view-value").click();
            await page
                .locator("li.byte-select-option", { hasText: TimeFormatter.format(params.settingInfo.timer, "h") })
                .click();
            await page.waitForTimeout(200);

            // 选择分钟
            await page.locator(".byte-modal-content .minute-select .byte-select-view-value").click();
            await page
                .locator("li.byte-select-option", { hasText: TimeFormatter.format(params.settingInfo.timer, "m") })
                .click();
            await page.waitForTimeout(200);

            await page.locator(".byte-modal-footer button").filter({ hasText: "预览并定时发布" }).click();
        } else {
            await page.locator(".publish-footer button").filter({ hasText: "预览并发布" }).click();
        }
    });

    if (params.settingInfo.toutiaoAd === "no") {
        await page.waitForTimeout(200);
        const confirmBtn = page.locator('div.byte-modal-footer button.byte-btn-primary:has-text("确定")');
        if (await confirmBtn.isVisible()) {
            await confirmBtn.click();
        }
    }

    task._timerRecord.PrePublish = Date.now();

    if (params.saveType === "publish") {
        await page
            .locator(".publish-footer button")
            .filter({ hasText: params.settingInfo.timer ? "定时发布" : "确认发布" })
            .waitFor({ state: "visible", timeout: 90000 });
        await page.waitForTimeout(200);
        await page
            .locator(".publish-footer button")
            .filter({ hasText: params.settingInfo.timer ? "定时发布" : "确认发布" })
            .click();
    }

    // 关闭页面
    await page.close();

    return success(articleId, params.saveType === "publish" ? "发布成功" : "保存草稿成功");
};
